import {
  BadgeTemplate,
  BadgeElement,
  BorderStyle,
  TransformStyle,
  PrinterModel,
  PrinterMedia,
  TemplateColor,
  BadgeQRFormat,
} from "sharedTypes/badge"
import {
  MediaColorMode,
  MediaDescriptor,
  MediaSize,
  PrinterDescriptor,
  getMediaDescriptor,
  getPrinterDescriptor,
} from "./printers"

export const getTemplateDimensions = (template: BadgeTemplate) => ({
  width: template.widthMm,
  height: template.heightMm,
})

export const updateTemplateElements = (
  template: BadgeTemplate,
  updater: (elements: BadgeElement[]) => BadgeElement[],
): BadgeTemplate => ({
  ...template,
  badgeData: {
    ...template.badgeData,
    elements: updater(template.badgeData.elements),
  },
})

export const DEFAULT_BORDER_STYLE: BorderStyle = {
  color: "#000000",
  width: 0,
  style: "solid",
}

export const DEFAULT_TRANSFORM_STYLE: TransformStyle = {
  rotate: 0,
  flipHorizontal: false,
  flipVertical: false,
}

export const createDefaultBorderStyle = (
  overrides?: Partial<BorderStyle>,
): BorderStyle => ({
  ...DEFAULT_BORDER_STYLE,
  ...overrides,
})

export const createDefaultTransformStyle = (
  overrides?: Partial<TransformStyle>,
): TransformStyle => ({
  ...DEFAULT_TRANSFORM_STYLE,
  ...overrides,
})

export type CombinedCapability = {
  printer: PrinterDescriptor
  media: MediaDescriptor
  printableSize: MediaSize
  colorMode: MediaColorMode
  supportsImages: boolean
  supportsQR: boolean
  maxResolutionDPI: number
}

export const allowedColors = (mode: MediaColorMode): TemplateColor[] => (mode === "full-color"
  ? ["black", "white", "red"] /* plus any hex in UI */
  : mode === "black-red"
    ? ["black", "white", "red"]
    : ["black", "white"])

export const getCapability = (
  printerModel: PrinterModel,
  printerMedia: PrinterMedia,
): CombinedCapability => {
  const printer = getPrinterDescriptor(printerModel)
  const media = getMediaDescriptor(printerMedia)

  if (!printer.supportedMedia.includes(printerMedia)) {
    throw new Error(`Printer ${printerModel} does not support media ${printerMedia}`)
  }

  return {
    printer,
    media,
    printableSize: media.size,
    colorMode: media.colorMode,
    supportsImages: media.supportsImages,
    supportsQR: media.supportsQR,
    maxResolutionDPI: media.maxResolutionDPI,
  }
}

type ContentOrientationRelativeToRollFeed = "parallel" | "perpendicular"

export const getContentOrientationRelativeToRollFeed = (
  template: Pick<BadgeTemplate, "widthMm" | "heightMm" | "printerModel" | "printerMedia">,
): ContentOrientationRelativeToRollFeed => {
  const { widthMm, heightMm } = template
  const capability = getCapability(template.printerModel, template.printerMedia)
  const size = capability.printableSize

  if (size.type !== "roll") {
    throw new Error("Orientation relative to roll feed is only available for roll media")
  }

  const rollWidth = size.widthMm

  if (Math.abs(widthMm - rollWidth) < 1) {
    return "perpendicular"
  }
  if (Math.abs(heightMm - rollWidth) < 1) {
    return "parallel"
  }

  return widthMm >= heightMm ? "parallel" : "perpendicular"
}

export const calculatePrintInstructions = (
  template: Pick<BadgeTemplate, "widthMm" | "heightMm" | "printerModel" | "printerMedia">,
): BadgeTemplate["printerOrientation"] => {
  const { widthMm, heightMm } = template
  const capability = getCapability(template.printerModel, template.printerMedia)
  const size = capability.printableSize

  if (size.type === "roll") {
    const contentOrientation = getContentOrientationRelativeToRollFeed(
      template,
    )

    return contentOrientation === "perpendicular" ? "portrait" : "landscape"
  }

  return widthMm > heightMm ? "landscape" : "portrait"
}

export const QR_MIN_SIZES_MM = {
  [BadgeQRFormat.GUEST_ID]: 10,
  [BadgeQRFormat.VCARD]: 23,
} as const

export const getQRCodeMinSize = (format: BadgeQRFormat): number => QR_MIN_SIZES_MM[format]

export const validateTemplateDimensions = (
  template: BadgeTemplate,
): { valid: boolean; error?: string } => {
  const capability = getCapability(template.printerModel, template.printerMedia)
  const { widthMm, heightMm } = template
  const size = capability.printableSize
  const approxEquals = (a: number, b: number, tolerance = 0.1) => Math.abs(a - b) < tolerance

  if (size.type === "roll") {
    const isWidthFixed = approxEquals(widthMm, size.widthMm)
    const isHeightFixed = approxEquals(heightMm, size.widthMm)

    if (!(isWidthFixed || isHeightFixed)) {
      return {
        valid: false,
        error: `One side must equal the roll width of ${size.widthMm}mm`,
      }
    }

    const adjustableSide = isWidthFixed ? heightMm : widthMm
    if (adjustableSide < size.minLengthMm || adjustableSide > size.maxLengthMm) {
      const sideName = isWidthFixed ? "Height" : "Width"

      return {
        valid: false,
        error: `${sideName} must be between ${size.minLengthMm}mm and ${size.maxLengthMm}mm`,
      }
    }

    return { valid: true }
  }

  if (size.type === "fixed") {
    if (!approxEquals(widthMm, size.widthMm) || !approxEquals(heightMm, size.heightMm)) {
      return {
        valid: false,
        error: `Dimensions must be exactly ${size.widthMm}mm × ${size.heightMm}mm`,
      }
    }

    return { valid: true }
  }

  const {
    minWidthMm,
    maxWidthMm,
    minHeightMm,
    maxHeightMm,
  } = size

  if (minWidthMm && widthMm < minWidthMm) {
    return {
      valid: false,
      error: `Width must be at least ${minWidthMm}mm`,
    }
  }

  if (maxWidthMm && widthMm > maxWidthMm) {
    return {
      valid: false,
      error: `Width must not exceed ${maxWidthMm}mm`,
    }
  }

  if (minHeightMm && heightMm < minHeightMm) {
    return {
      valid: false,
      error: `Height must be at least ${minHeightMm}mm`,
    }
  }

  if (maxHeightMm && heightMm > maxHeightMm) {
    return {
      valid: false,
      error: `Height must not exceed ${maxHeightMm}mm`,
    }
  }

  if (widthMm < 1 || heightMm < 1) {
    return {
      valid: false,
      error: "Width and height must be at least 1mm",
    }
  }

  return { valid: true }
}

export const sortBadgeTemplates = (templates: BadgeTemplate[]):
BadgeTemplate[] => [...templates].sort((a, b) => {
  const nameCompare = a.name.localeCompare(b.name, undefined, {
    numeric: true,
    sensitivity: "base",
  })
  if (nameCompare !== 0) {
    return nameCompare
  }

  return a.id.localeCompare(b.id)
})
